import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useEffect } from 'react';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '电影频道', to: '/category/movie' },
  { label: '电视剧频道', to: '/category/tv' },
  { label: '关于我们', to: '/about' },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setKeyword(params.get('q') ?? '');
  }, [location.search]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur border-b border-background-200' : 'bg-primary-950/85 backdrop-blur border-b border-primary-900/40'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-500 text-primary-950">
              <i className="ri-movie-2-line text-xl"></i>
            </span>
            <span className={`font-heading font-bold text-lg tracking-tight whitespace-nowrap ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}>
              朋友的妈妈在线
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => {
                  const base = 'px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer';
                  const activeCls = scrolled
                    ? 'text-primary-700 bg-primary-100'
                    : 'text-accent-400 bg-primary-900/60';
                  const idleCls = scrolled
                    ? 'text-foreground-700 hover:text-primary-700 hover:bg-background-100'
                    : 'text-background-100 hover:text-accent-300 hover:bg-primary-900/50';
                  return `${base} ${isActive ? activeCls : idleCls}`;
                }}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 justify-end">
            <div
              className={`flex items-center gap-2 rounded-full pl-4 pr-1 py-1 w-full max-w-xs border transition-colors ${
                scrolled ? 'bg-background-100 border-background-200' : 'bg-primary-900/60 border-primary-800'
              }`}
            >
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视名称或关键词"
                className={`flex-1 bg-transparent outline-none text-sm ${
                  scrolled ? 'text-foreground-900 placeholder:text-foreground-400' : 'text-background-50 placeholder:text-background-300'
                }`}
              />
              <button
                type="submit"
                aria-label="搜索"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 text-primary-950 hover:bg-accent-600 transition-colors cursor-pointer"
              >
                <i className="ri-search-line text-base"></i>
              </button>
            </div>
          </form>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className={`md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md ${scrolled ? 'text-foreground-900' : 'text-background-50'}`}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 space-y-3">
          <form onSubmit={submitSearch} className="flex items-center gap-2 rounded-full bg-background-100 border border-background-200 pl-4 pr-1 py-1">
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视名称或关键词"
              className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
            />
            <button type="submit" aria-label="搜索" className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-500 text-primary-950 cursor-pointer">
              <i className="ri-search-line"></i>
            </button>
          </form>
          <nav className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm font-medium cursor-pointer ${
                    isActive ? 'text-primary-700 bg-primary-100' : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}